import Autocomplete from "@mui/material/Autocomplete";
import { useTranslations } from "adapters/i18n";
import React, { useEffect, useState } from "react";
import Input from "./components/Input";
import Option from "./components/Option";

const DEFAULT_STYLES = {
  width: "100%",
  backgroundColor: "#ffffff",
};

function SelectDropdown(props) {
  const {
    className,
    emptyText,
    error,
    groupBy,
    helperText,
    id,
    isClearable,
    isDisabled,
    isLoading,
    isMulti,
    isSearchable,
    label,
    onChange,
    onPaginate,
    onPagineThresholdInPorcentage = 0.9,
    onSearch,
    options = [],
    overrideStrings,
    renderOptionComponent,
    size = "medium",
    styles,
    testId,
    tooltipMessage,
    value,
    variant,
    ...rest
  } = props;

  const { t } = useTranslations();

  const defaultStrings = {
    noOptions: t("common:no-options-available"),
  };

  const noOptionsStringText = { ...defaultStrings, ...overrideStrings };

  const [_options, _setOptions] = useState(options);
  const handleChange = (_, newValue) => {
    if (!newValue) return onChange(null);
    if (isMulti) {
      return onChange(newValue.map((item) => item.value || item));
    }
    return onChange(newValue.value);
  };

  const handleInputChange = (event) => {
    if (!event) return;
    onSearch
      ? onSearch(event.target.value || "")
      : handleSearch(event.target.value || "");
  };

  const onScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    const currentScroll = scrollTop + clientHeight;
    const scrollThreshold = scrollHeight * onPagineThresholdInPorcentage;

    if (currentScroll >= scrollThreshold) {
      onPaginate && onPaginate();
    }
  };

  const getOptionDisabled = (option) => option.disabled;

  const getOptionLabel = (option) => {
    if (option.label) return option.label;
    const optionSelected = (options || []).find((o) => o.value === option);
    return optionSelected ? optionSelected.label : option;
  };

  const isOptionEqualToValue = (option, value) => {
    if (value == null) return false;
    return String(option.value) === String(value.value || value);
  };

  const handleSearch = (searchText) => {
    const processOptions = options.map((option) => {
      if (
        option.label &&
        option.label.toLowerCase().includes(searchText.toLowerCase())
      ) {
        return option;
      }
      return {
        ...option,
        visible: false,
      };
    });
    _setOptions(processOptions);
  };

  const renderOption = (props, option, { selected }) =>
    option.visible === false ? null : (
      <Option
        key={option.value}
        isMulti={isMulti}
        props={props}
        option={option}
        selected={selected}
      />
    );
  const renderInput = (params) => (
    <Input
      {...params}
      emptyText={emptyText}
      error={error}
      helperText={helperText}
      isLoading={isLoading}
      isSearchable={isSearchable}
      label={label}
      tooltipMessage={tooltipMessage}
      variant={variant}
    />
  );

  useEffect(() => {
    _setOptions(options);
  }, [options]);

  return (
    <Autocomplete
      blurOnSelect={!isMulti}
      className={className}
      data-testid={testId}
      disableClearable={!isClearable}
      disableCloseOnSelect={isMulti}
      disabled={isDisabled}
      disablePortal={false}
      getOptionDisabled={getOptionDisabled}
      getOptionLabel={getOptionLabel}
      groupBy={groupBy}
      id={id}
      isOptionEqualToValue={isOptionEqualToValue}
      labelid={label && label.id}
      ListboxProps={{ onScroll }}
      loading={isLoading}
      multiple={isMulti}
      noOptionsText={noOptionsStringText.noOptions}
      onChange={handleChange}
      onInputChange={handleInputChange}
      openOnFocus={true}
      options={_options}
      renderInput={renderInput}
      renderOption={renderOptionComponent || renderOption}
      selectOnFocus={false}
      size={size}
      sx={{ ...DEFAULT_STYLES, ...styles }}
      value={value}
      {...rest}
    />
  );
}

export default SelectDropdown;
